/**
 * OVERRIDE BlocksToolbar.jsx
 * REASON: Upstream regression from plone/volto#7925: the store persists the
 *         clipboard under the dotted keys
 *         `redux_localstorage_simple_blocksClipboard.cut` / `.copy`, but
 *         loadFromStorage (storage-event sync between tabs) still read the
 *         pre-#7925 key `redux_localstorage_simple_blocksClipboard`. Any
 *         storage write from another same-origin tab therefore replaced a
 *         fresh copy with the stale legacy entry (pasting months-old blocks)
 *         or cleared the clipboard.
 * FILE: https://github.com/plone/volto/blob/main/packages/volto/src/components/manage/Form/BlocksToolbar.jsx
 * FILE VERSION: Volto 18.32.4
 * TICKET: #1707, #1728
 * DATE: 2026-07-20, extended 2026-08-04
 * DEVELOPER: Michael Perret
 * CHANGELOG:
 *  - loadFromStorage reads the dotted keys via loadBlocksClipboardFromStorage
 *    (replaces the upstream `load` import from redux-localstorage-simple)
 *  - componentDidMount removes the legacy key so pre-upgrade copies cannot
 *    resurface anywhere
 *  - #1728 (follow-up to the !1466 review): loadFromStorage now takes the
 *    StorageEvent and ignores keys that are not the clipboard's, and adopts
 *    only the changed entry from `event.newValue` instead of re-reading both
 *    keys (re-reading joined a stale `.cut` to a fresh `.copy`, and
 *    pasteBlocks prefers `cut` whenever present).
 *  - #1728: storage events arriving within the store's 500 ms persist debounce
 *    (volto/src/store.js:29) after our own copy/cut are ignored — until the
 *    flush our in-memory clipboard is the newest state there is.
 *  - #1728: componentWillUnmount passes the capture flag, so the listener is
 *    actually removed instead of piling up across remounts.
 *
 * Remove this shadow once the upstream fix ships in our Volto version.
 *
 * Depending on the nature of the change, if possible or suitable, mark the change with a JSX comment at the beginning and end of the change:
 *
 *   START CUSTOMIZATION
 *   <CUSTOMIZATION>
 *   END CUSTOMIZATION
 */
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { messages } from '@plone/volto/helpers/MessageLabels/MessageLabels';
import {
  getBlocksFieldname,
  getBlocksLayoutFieldname,
} from '@plone/volto/helpers/Blocks/Blocks';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { Plug } from '@plone/volto/components/manage/Pluggable';
import { v4 as uuid } from 'uuid';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import without from 'lodash/without';

import {
  setBlocksClipboard,
  resetBlocksClipboard,
} from '@plone/volto/actions/blocksClipboard/blocksClipboard';
import config from '@plone/volto/registry';
// START CUSTOMIZATION
import {
  applyBlocksClipboardStorageEvent,
  removeLegacyBlocksClipboard,
  BLOCKS_CLIPBOARD_PERSIST_DEBOUNCE_MS,
} from '@fhnw/helpers/BlocksClipboard/blocksClipboard';
// END CUSTOMIZATION

import copySVG from '@plone/volto/icons/copy.svg';
import cutSVG from '@plone/volto/icons/cut.svg';
import pasteSVG from '@plone/volto/icons/paste.svg';
import trashSVG from '@plone/volto/icons/delete.svg';
import { cloneBlocks } from '@plone/volto/helpers/Blocks/cloneBlocks';

export class BlocksToolbarComponent extends React.Component {
  constructor(props) {
    super(props);

    this.copyBlocksToClipboard = this.copyBlocksToClipboard.bind(this);
    this.cutBlocksToClipboard = this.cutBlocksToClipboard.bind(this);
    this.deleteBlocks = this.deleteBlocks.bind(this);
    this.loadFromStorage = this.loadFromStorage.bind(this);
    this.pasteBlocks = this.pasteBlocks.bind(this);
    this.setBlocksClipboard = this.setBlocksClipboard.bind(this);
    // START CUSTOMIZATION
    // When we last wrote the clipboard ourselves — see loadFromStorage (#1728)
    this.lastLocalClipboardWrite = 0;
    // END CUSTOMIZATION
  }

  loadFromStorage(event) {
    // START CUSTOMIZATION
    // Our own copy/cut only reaches localStorage after the store's 500 ms
    // debounce. Until then our in-memory clipboard is the newest state there
    // is, so any storage event in that window describes something older and
    // must not overwrite it (#1728).
    if (
      Date.now() - this.lastLocalClipboardWrite <
      BLOCKS_CLIPBOARD_PERSIST_DEBOUNCE_MS
    ) {
      return;
    }
    // Upstream re-read both clipboard keys on every same-origin storage
    // event. Apply only the entry the event changed instead.
    const clipboard = applyBlocksClipboardStorageEvent(
      this.props.blocksClipboard || {},
      event || {},
    );
    if (clipboard === null) {
      return;
    }
    // END CUSTOMIZATION
    if (!isEqual(clipboard, this.props.blocksClipboard))
      this.props.setBlocksClipboard(clipboard || {});
  }

  componentDidMount() {
    // START CUSTOMIZATION
    removeLegacyBlocksClipboard();
    // END CUSTOMIZATION
    window.addEventListener('storage', this.loadFromStorage, true);
  }

  componentWillUnmount() {
    // START CUSTOMIZATION
    // Upstream omits the capture flag here, so the listener survived every
    // unmount and piled up across remounts (!1466 review).
    window.removeEventListener('storage', this.loadFromStorage, true);
    // END CUSTOMIZATION
  }

  deleteBlocks() {
    const blockIds = this.props.selectedBlocks;

    const { formData } = this.props;
    const blocksFieldname = getBlocksFieldname(formData);
    const blocksLayoutFieldname = getBlocksLayoutFieldname(formData);

    // Might need ReactDOM.unstable_batchedUpdates()
    this.props.onSelectBlock(null);
    const newBlockData = {
      [blocksFieldname]: omit(formData[blocksFieldname], blockIds),
      [blocksLayoutFieldname]: {
        ...formData[blocksLayoutFieldname],
        items: without(formData[blocksLayoutFieldname].items, ...blockIds),
      },
    };
    this.props.onChangeBlocks(newBlockData);
  }

  copyBlocksToClipboard() {
    this.setBlocksClipboard('copy');
  }

  cutBlocksToClipboard() {
    this.setBlocksClipboard('cut');
    this.deleteBlocks();
  }

  setBlocksClipboard(actionType) {
    const { formData } = this.props;
    const blocksFieldname = getBlocksFieldname(formData);
    const blocks = formData[blocksFieldname];
    const blocksData = this.props.selectedBlocks
      .map((blockId) => [blockId, blocks[blockId]])
      .filter(([blockId]) => !!blockId); // Removes null blocks
    // START CUSTOMIZATION
    this.lastLocalClipboardWrite = Date.now();
    // END CUSTOMIZATION
    this.props.setBlocksClipboard({ [actionType]: blocksData });
    this.props.onSetSelectedBlocks([]);
  }

  pasteBlocks(e) {
    const { formData, blocksClipboard = {}, selectedBlock } = this.props;
    const mode = Object.keys(blocksClipboard).includes('cut') ? 'cut' : 'copy';
    const blocksData = blocksClipboard[mode] || [];
    const cloneWithIds = blocksData
      .filter(([blockId, blockData]) => blockId && !!blockData['@type']) // Removes null blocks
      .map(([blockId, blockData]) => {
        const blockConfig = config.blocks.blocksConfig[blockData['@type']];
        return mode === 'copy'
          ? blockConfig.cloneData
            ? blockConfig.cloneData(blockData)
            : [uuid(), cloneBlocks(blockData)]
          : [blockId, blockData]; // if cut/pasting blocks, we don't clone
      })
      .filter((info) => !!info); // some blocks may refuse to be copied
    const blocksFieldname = getBlocksFieldname(formData);
    const blocksLayoutFieldname = getBlocksLayoutFieldname(formData);
    const selectedIndex =
      formData[blocksLayoutFieldname].items.indexOf(selectedBlock) + 1;

    const newBlockData = {
      [blocksFieldname]: {
        ...formData[blocksFieldname],
        ...Object.assign(
          {},
          ...cloneWithIds.map(([id, data]) => ({ [id]: data })),
        ),
      },
      [blocksLayoutFieldname]: {
        ...formData[blocksLayoutFieldname],
        items: [
          ...formData[blocksLayoutFieldname].items.slice(0, selectedIndex),
          ...cloneWithIds.map(([id]) => id),
          ...formData[blocksLayoutFieldname].items.slice(selectedIndex),
        ],
      },
    };

    if (!(e.ctrlKey || e.metaKey)) this.props.resetBlocksClipboard();
    this.props.onChangeBlocks(newBlockData);
  }

  render() {
    const {
      blocksClipboard = {},
      selectedBlock,
      selectedBlocks,
      intl,
    } = this.props;
    return (
      <>
        {selectedBlocks.length > 0 ? (
          <>
            <Plug pluggable="main.toolbar.bottom" id="blocks-delete-btn">
              <button
                aria-label={intl.formatMessage(messages.deleteBlocks)}
                onClick={this.deleteBlocks}
                tabIndex={0}
                className="deleteBlocks"
                id="toolbar-delete-blocks"
              >
                <Icon name={trashSVG} size="30px" />
              </button>
            </Plug>
            <Plug pluggable="main.toolbar.bottom" id="blocks-cut-btn">
              <button
                aria-label={intl.formatMessage(messages.cutBlocks)}
                onClick={this.cutBlocksToClipboard}
                tabIndex={0}
                className="cutBlocks"
                id="toolbar-cut-blocks"
              >
                <Icon name={cutSVG} size="30px" />
              </button>
            </Plug>
            <Plug pluggable="main.toolbar.bottom" id="blocks-copy-btn">
              <button
                aria-label={intl.formatMessage(messages.copyBlocks)}
                onClick={this.copyBlocksToClipboard}
                tabIndex={0}
                className="copyBlocks"
                id="toolbar-copy-blocks"
              >
                <Icon name={copySVG} size="30px" />
              </button>
            </Plug>
          </>
        ) : (
          ''
        )}
        {selectedBlock && (blocksClipboard?.cut || blocksClipboard?.copy) && (
          <Plug
            pluggable="main.toolbar.bottom"
            id="block-paste-btn"
            dependencies={[selectedBlock]}
          >
            <button
              aria-label={intl.formatMessage(messages.pasteBlocks)}
              onClick={this.pasteBlocks}
              tabIndex={0}
              className="pasteBlocks"
              id="toolbar-paste-blocks"
            >
              <span className="blockCount">
                {(blocksClipboard.cut || blocksClipboard.copy).length}
              </span>
              <Icon name={pasteSVG} size="30px" />
            </button>
          </Plug>
        )}
      </>
    );
  }
}

export default compose(
  injectIntl,
  connect(
    (state) => {
      return {
        blocksClipboard: state?.blocksClipboard || {},
      };
    },
    { setBlocksClipboard, resetBlocksClipboard },
  ),
)(BlocksToolbarComponent);
